import TextArea from "../../../Components/Common/TextArea/TextArea";
import Select from "Components/Common/Select/Select";
import { openMailTo, openTelTo } from "../../../utils/Common/contact";
import MailIcon from "Assets/Img/MailIcon";
import PhoneIcon from "Assets/Img/PhoneIcon";
import { CHAR_LIMIT } from "../../../utils/Common/Constant";
import { useEffect, useRef, useState } from "react";
import { getLenderProjectOverview } from "services/projectService";
import { getConversations, sendMessage } from "services/messagesService";
import Skeleton from "Components/Common/Skeleton/Skeleton";
import Button from "Components/Common/Button/Button";

type ModalType = "approve" | "reject" | "comment";

const SUBJECT_OPTIONS = [
  { value: "Question about Draw #06", label: "Question about Draw #06" },
  { value: "Request a re-inspection", label: "Request a re-inspection" },
  {
    value: "Insurance certificate question",
    label: "Insurance certificate question",
  },
  {
    value: "Schedule / milestone question",
    label: "Schedule / milestone question",
  },
  { value: "Other", label: "Other" },
];

interface StakeholderPerson {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  companyName?: string;
  title?: string;
}

interface CSCContactSectionProps {
  setOpenModal: (modal: ModalType | null) => void;
  showToast: (msg: string) => void;
  projectId?: string;
}

const fullName = (p?: StakeholderPerson) =>
  [p?.firstName, p?.lastName].filter(Boolean).join(" ") || "—";

const initials = (p?: StakeholderPerson) =>
  ((p?.firstName?.[0] ?? "") + (p?.lastName?.[0] ?? "")).toUpperCase();

export default function CSCContactSection({
  projectId,
  showToast,
}: CSCContactSectionProps) {
  const [admin, setAdmin] = useState<StakeholderPerson | null>(null);
  const [inspectors, setInspectors] = useState<StakeholderPerson[]>([]);
  const [loading, setLoading] = useState(true);
  const [subject, setSubject] = useState("Question about Draw #06");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!projectId || fetchedRef.current) return;
    fetchedRef.current = true;
    Promise.all([
      getLenderProjectOverview(projectId) as Promise<any>,
      getConversations(projectId) as Promise<any>,
    ]).then(([overview, convRes]) => {
      if (!overview.error && overview.data?.data?.stakeholders) {
        const s = overview.data.data.stakeholders;
        if (s.admin) setAdmin(s.admin);
        if (Array.isArray(s.inspectors)) setInspectors(s.inspectors);
      }
      const conversations = convRes?.data?.data;
      if (Array.isArray(conversations)) {
        const conv = conversations.find(
          (c: any) => c.name === "admin_lender" || c.name === "lender_admin",
        );
        if (conv) setConversationId(conv.id);
      }
      setLoading(false);
    });
  }, [projectId]);

  const contacts = [
    ...(admin
      ? [
          {
            person: admin,
            role: `${admin.companyName ?? "CSC"} — Project Coordinator`,
          },
        ]
      : []),
    ...inspectors.map((i) => ({
      person: i,
      role: `${i.companyName ?? "CSC"} — Inspector`,
    })),
  ];

  return (
    <div className="ds active">
      <div className="dsHeaderTitle">Contact CSC New England</div>

      {loading ? (
        <>
          {[1, 2].map((n) => (
            <div className="csc-card" key={n}>
              <Skeleton width={46} height={46} borderRadius={23} />
              <div style={{ flex: 1 }}>
                <Skeleton width="40%" height={11} style={{ marginBottom: 6 }} />
                <Skeleton width="70%" height={14} style={{ marginBottom: 6 }} />
                <Skeleton width="55%" height={11} />
              </div>
            </div>
          ))}
        </>
      ) : (
        <>
          {contacts.map(({ person, role }, idx) => (
            <div className="csc-card" key={role}>
              <div
                className="csc-av"
                style={idx > 0 ? { background: "var(--bm)" } : undefined}
              >
                {initials(person)}
              </div>
              <div className="csc-info">
                <div className="csc-name">{fullName(person)}</div>
                <div className="csc-role">
                  {person.title ? `${person.title} · ${role}` : role}
                </div>
                {(person.phone || person.email) && (
                  <div className="cscContactInfo">
                    {person.phone && (
                      <span>
                        <PhoneIcon style={{ verticalAlign: "middle" }} />{" "}
                        {person.phone}
                      </span>
                    )}
                    {person.phone && person.email && <span> · </span>}
                    {person.email && (
                      <span>
                        <MailIcon style={{ verticalAlign: "middle" }} />{" "}
                        {person.email}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="csc-actions">
                {person.email && (
                  <Button
                    className="btn btn-sm btn-primary"
                    onClick={() => openMailTo(person.email)}
                  >
                    <MailIcon style={{ verticalAlign: "middle" }} /> Email
                  </Button>
                )}
                {person.phone && (
                  <Button
                    className="btn btn-sm"
                    onClick={() => openTelTo(person.phone)}
                  >
                    <PhoneIcon /> Call
                  </Button>
                )}
              </div>
            </div>
          ))}
        </>
      )}

      <div className="card" style={{ marginTop: 14 }}>
        <div className="card-title">Send a Message to CSC</div>
        <Select
          options={SUBJECT_OPTIONS}
          style={{
            width: "100%",
            height: 36,
            border: "1px solid var(--bd)",
            borderRadius: 7,
            padding: "0 10px",
            fontSize: 13,
            color: "var(--tx)",
            background: "var(--bg2)",
            fontFamily: "system-ui, sans-serif",
            outline: "none",
            marginBottom: 10,
          }}
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        />
        <TextArea
          className="comment-ta mb-0"
          placeholder="Type your message to CSC New England…"
          style={{ minHeight: 100, marginBottom: 10 }}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          maxChars={CHAR_LIMIT.DESCRIPTION}
        />
        <Button
          className="btn btn-primary mt-2"
          style={{ width: "max-content" }}
          disabled={
            sending ||
            !message.trim() ||
            !conversationId ||
            message.length > CHAR_LIMIT.DESCRIPTION
          }
          onClick={async () => {
            if (!projectId || !conversationId) return;
            setSending(true);
            const body = `${subject}: ${message.trim()}`;
            const res = await (sendMessage(
              projectId,
              conversationId,
              body,
            ) as Promise<any>);
            setSending(false);
            if (!res.error) {
              showToast("Message sent to CSC New England");
              setMessage("");
            } else {
              showToast("Failed to send message. Please try again.");
            }
          }}
        >
          {sending ? "Sending…" : "Send Message"}
        </Button>
      </div>
    </div>
  );
}
